import React, { FC, useEffect, useState } from 'react';
import Modal from 'molecules/Modal';
import { ModalContent, ModalSubtitleText } from 'molecules/Modal/Modal.styles';
import { Box, Stack } from '@mui/material';

// import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import loaderGif from 'assets/images/loaderGif.gif';
import { generateCostReportPDF } from 'organisms/OrgReportPDF/export';
import { StyledLoadingButton } from './OrgReportPreview.styles';
import { mapOrgReportToCostReportData } from 'organisms/OrgReportPDF/helpers';
import { ObservabilityReportResponse } from 'organisms/CloudOpsReport/types';

interface InvoicePreviewProps {
  report: ObservabilityReportResponse;
  onClose: () => void;
}

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const ReportPreview: FC<InvoicePreviewProps> = ({ report, onClose }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const [url, setUrl] = useState<string>();
  const [pages, setPages] = useState<number>(0);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setPages(numPages);
  };

  const onDownload = () => {
    if (!url) return;

    try {
      setLoading(true);

      const link = document.createElement('a');
      link.href = url; // response.request?.responseURL;
      link.setAttribute('download', 'org_report.pdf');
      document.body.appendChild(link);
      link.click();
    } finally {
      setLoading(false);
    }
  };

  const renderSubtitle = () => (
    <Stack gap="16px">
      <ModalSubtitleText>
        {pages ? `${pages} ${pages > 1 ? 'pages' : 'page'}` : ''}
      </ModalSubtitleText>
      {!loading && pages ? (
        <StyledLoadingButton onClick={onDownload}>
          Download PDF
        </StyledLoadingButton>
      ) : (
        ''
      )}
    </Stack>
  );

  useEffect(() => {
    if (!report) return;
    showPdf();
  }, [report]);

  useEffect(() => () => URL.revokeObjectURL(url || ''), []);

  async function showPdf() {
    if (!report.id) return;

    try {
      setLoading(true);

      const mapped = mapOrgReportToCostReportData(report.report);

      const blobData = await generateCostReportPDF(mapped);
      setUrl(blobData);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open
      maxWidth="md"
      title="Infrastructure Observability Report"
      subtitle={
        loading || !pages ? <Box sx={{ height: '94px' }} /> : renderSubtitle()
      }
      onClose={onClose}
      sx={{
        zIndex: 33333,
        '.MuiPaper-root': {
          width: '600px !important',
          background: 'transparent',
          boxShadow: 'none',
          borderRadius: 0,
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          '& > div:first-of-type': {
            borderRadius: '4px',
          },
        },
        '.modal-title-block': {
          background: '#FFFFFF',
        },
      }}
    >
      <ModalContent
        sx={{
          p: '0px !important',
          minHeight: '80px',
          overflowX: 'hidden',
          '.react-pdf__Page__canvas': {
            width: '600px !important',
          },
          '.react-pdf__Page.pdf-page': {
            mb: '10px',
          },
        }}
      >
        {!loading && url ? (
          // eslint-disable-next-line
          // @ts-ignore
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <Box
                sx={{
                  textAlign: 'center',
                  padding: '40px 0',
                  background: '#FFFFFF !important',
                }}
              >
                <img src={loaderGif} alt="loader" />
              </Box>
            }
          >
            {Array.from({ length: pages }, (_, index) => (
              // eslint-disable-next-line
              // @ts-ignore
              <Page
                key={index + 1}
                pageNumber={index + 1}
                className="pdf-page"
              />
            ))}
          </Document>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              padding: '40px 0',
              background: '#FFFFFF !important',
            }}
          >
            <img src={loaderGif} alt="loader" />
          </Box>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ReportPreview;
